import { useMemo } from 'react'
import createCache from '@emotion/cache'
import { CacheProvider, ThemeProvider, Theme } from '@emotion/react'
import { fusionScreenTheme, fusionPrintThemeOverrides } from './theme/FusionStyle'
import { useFormSelector } from './redux/formStoreHooks'
import { FormStyleRoot } from './theme/FormStyleRoot'
import { RenderMedia } from '@fusion-advantage/backcompat'
import L2Interface, { L2InterfaceMode } from './stories/interface/L2Interface'
import { L1StatusBarBackground } from './stories/interface/L1StatusBarBackground'
import { V2LxFormAppHeader } from './stories/uiv2/AppHeader/V2LxFormAppHeader'
import { randomLowercaseId } from '@fusion-advantage/formlib'
import FormRoot from './FormRoot'
import L4LeaveBehindDialog from './stories/form/l4/L4LeaveBehindDialog'
import Loading from './stories/Loading'

const Form = () => {
	const formLoading = useFormSelector(state => state.render.formLoading)
	const media = useFormSelector(state => state.render.media)
	const renderTheme = useFormSelector(state => state.render.theme)

	// Trying to get multiple instances of Emotion
	// (frontend, plus multiple formrender versions in same session...)
	// to play as nice as possible on the same page.
	// https://github.com/emotion-js/emotion/issues/2210
	// I suppose creating a new cache for each formrender instance could be
	// problematic if you open a whole lot of forms without refreshing the
	// Window, but let's see if it ends up being a real problem.  Perhaps
	// we can manually sweep out <style /> tags on unmount if necessary.
	const emotionCache = useMemo(() => {
		return createCache({
			key: 'css' + randomLowercaseId(),
		})
	}, [])

	const theme = useMemo((): Theme => {
		return {
			...fusionScreenTheme,
			...(media === RenderMedia.Print ? fusionPrintThemeOverrides : {}),
			...renderTheme,
		}
	}, [media, renderTheme])

	return (
		<CacheProvider value={emotionCache}>
			<ThemeProvider theme={theme}>
				<FormStyleRoot>
					<L2Interface
						mode={(media === RenderMedia.Print ? L2InterfaceMode.Print : L2InterfaceMode.Scrollable)}
						statusBar={<L1StatusBarBackground />}
						header={<V2LxFormAppHeader />}
					>
						{formLoading ? (
							<Loading />
						) : (
							<FormRoot />
						)}
						<L4LeaveBehindDialog />
					</L2Interface>
				</FormStyleRoot>
			</ThemeProvider>
		</CacheProvider>
	)
}

export default Form
